/* ==========================================================================
   B2B Line Sheet – Custom Elements
   ========================================================================== */

/**
 * b2b-line-row
 * Handles variant switching, quantity stepper, and add-to-cart for each row.
 */
if (!customElements.get('b2b-line-row')) {
  customElements.define('b2b-line-row', class B2BLineRow extends HTMLElement {
    constructor() {
      super();
      this.variants = JSON.parse(this.dataset.variants || '[]');
      this.variantSelect = this.querySelector('.b2b-row__variant-select');
      this.variantInput = this.querySelector('input[name="id"]');
      this.priceEl = this.querySelector('.b2b-row__price-current');
      this.comparePriceEl = this.querySelector('.b2b-row__price-compare');
      this.skuEl = this.querySelector('.b2b-row__sku-value');
      this.stockEl = this.querySelector('.b2b-row__stock');
      this.qtyInput = this.querySelector('.b2b-qty-stepper__input');
      this.minusBtn = this.querySelector('.b2b-qty-stepper__btn--minus');
      this.plusBtn = this.querySelector('.b2b-qty-stepper__btn--plus');
      this.addBtn = this.querySelector('.b2b-row__add-btn');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    connectedCallback() {
      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', this.onVariantChange.bind(this));
      }

      if (this.minusBtn) {
        this.minusBtn.addEventListener('click', () => this.adjustQty(-1));
      }

      if (this.plusBtn) {
        this.plusBtn.addEventListener('click', () => this.adjustQty(1));
      }

      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => this.validateQty());
      }

      if (this.addBtn) {
        this.addBtn.addEventListener('click', this.onAddToCart.bind(this));
      }

      this.updateStepperBounds();
    }

    getCurrentVariant() {
      const selectedId = this.variantSelect
        ? parseInt(this.variantSelect.value)
        : parseInt(this.variantInput?.value);
      return this.variants.find(v => v.id === selectedId) || this.variants[0];
    }

    onVariantChange() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      // Update hidden input
      if (this.variantInput) this.variantInput.value = variant.id;

      // Update price display
      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(variant.price);
      }

      // Update compare-at price
      if (this.comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          this.comparePriceEl.style.display = '';
        } else {
          this.comparePriceEl.style.display = 'none';
        }
      }

      // Update SKU
      if (this.skuEl) {
        this.skuEl.textContent = variant.sku || '—';
      }

      // Update stock status
      if (this.stockEl) {
        if (variant.available) {
          this.stockEl.textContent = 'In Stock';
          this.stockEl.className = 'b2b-row__stock b2b-row__stock--in';
        } else {
          this.stockEl.textContent = 'Sold Out';
          this.stockEl.className = 'b2b-row__stock b2b-row__stock--out';
        }
      }

      // Update add-to-cart button state
      if (this.addBtn) {
        this.addBtn.disabled = !variant.available;
        const textEl = this.addBtn.querySelector('.b2b-row__add-text');
        if (textEl) {
          textEl.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        }
      }

      // Update quantity bounds for this variant
      this.updateStepperBounds();
    }

    updateStepperBounds() {
      const variant = this.getCurrentVariant();
      if (!variant || !this.qtyInput) return;

      const min = variant.quantity_rule?.min || 1;
      const max = variant.quantity_rule?.max || 9999;
      const step = variant.quantity_rule?.increment || 1;

      this.qtyInput.min = min;
      this.qtyInput.max = max;
      this.qtyInput.step = step;

      // If current value is below min, set to min
      const currentVal = parseInt(this.qtyInput.value) || min;
      if (currentVal < min) this.qtyInput.value = min;

      this.updateBtnStates();
    }

    adjustQty(direction) {
      if (!this.qtyInput) return;

      const variant = this.getCurrentVariant();
      const min = variant?.quantity_rule?.min || 1;
      const max = variant?.quantity_rule?.max || 9999;
      const step = variant?.quantity_rule?.increment || 1;

      let current = parseInt(this.qtyInput.value) || min;
      let newVal = current + (step * direction);

      newVal = Math.max(min, Math.min(max, newVal));
      this.qtyInput.value = newVal;

      this.updateBtnStates();
    }

    validateQty() {
      if (!this.qtyInput) return;

      const variant = this.getCurrentVariant();
      const min = variant?.quantity_rule?.min || 1;
      const max = variant?.quantity_rule?.max || 9999;
      const step = variant?.quantity_rule?.increment || 1;

      let val = parseInt(this.qtyInput.value) || min;

      // Round to nearest valid increment
      val = Math.round((val - min) / step) * step + min;
      val = Math.max(min, Math.min(max, val));

      this.qtyInput.value = val;
      this.updateBtnStates();
    }

    updateBtnStates() {
      if (!this.qtyInput) return;

      const variant = this.getCurrentVariant();
      const min = variant?.quantity_rule?.min || 1;
      const max = variant?.quantity_rule?.max || 9999;
      const current = parseInt(this.qtyInput.value) || min;

      if (this.minusBtn) this.minusBtn.disabled = current <= min;
      if (this.plusBtn) this.plusBtn.disabled = current >= max;
    }

    async onAddToCart(e) {
      e.preventDefault();
      const variant = this.getCurrentVariant();
      if (!variant || !variant.available) return;

      const quantity = parseInt(this.qtyInput?.value) || 1;

      // Set loading state
      this.addBtn.disabled = true;
      this.addBtn.classList.add('b2b-row__add-btn--loading');

      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json';

        const body = {
          id: variant.id,
          quantity: quantity
        };

        const response = await fetch(window.routes.cart_add_url, {
          ...config,
          body: JSON.stringify(body)
        });

        const data = await response.json();

        if (data.status) {
          // Error (e.g. not enough inventory)
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'b2b-line-sheet',
              productVariantId: variant.id,
              errors: data.description,
              message: data.message
            });
          }
          console.error('Add to cart error:', data.description);
          return;
        }

        // Publish cart update event for header badge etc.
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'b2b-line-sheet',
            productVariantId: variant.id
          });
        }

        // Flash the row green briefly
        this?.classList.add('b2b-row--added');
        setTimeout(() => {
          this?.classList.remove('b2b-row--added');
        }, 800);

        // Update floating order bar
        const orderBar = document.querySelector('b2b-order-bar');
        if (orderBar) orderBar.refresh();

      } catch (err) {
        console.error('Add to cart failed:', err);
      } finally {
        this.addBtn.disabled = false;
        this.addBtn.classList.remove('b2b-row__add-btn--loading');
      }
    }

    formatMoney(cents) {
      // Use Shopify's money format if available, otherwise simple USD format
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      }
      const amount = (cents / 100).toFixed(2);
      return '$' + amount;
    }
  });
}


/**
 * Sticky header – scroll direction detection
 * Shows the sticky table header only when scrolling DOWN, hides it when scrolling UP
 * so it doesn't collide with the site header.
 */
(function() {
  let lastScrollY = window.scrollY;
  let ticking = false;

  function onScroll() {
    const header = document.querySelector('.b2b-table__header');
    if (!header) return;

    const currentScrollY = window.scrollY;

    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      // Scrolling DOWN and past the top — show sticky header
      header.classList.remove('b2b-table__header--hidden');
    } else {
      // Scrolling UP — hide sticky header so it doesn't overlap site nav
      header.classList.add('b2b-table__header--hidden');
    }

    lastScrollY = currentScrollY;
    ticking = false;
  }

  window.addEventListener('scroll', function() {
    if (!ticking) {
      requestAnimationFrame(onScroll);
      ticking = true;
    }
  }, { passive: true });
})();


/**
 * b2b-order-bar
 * Floating sticky bar showing running cart total.
 */
if (!customElements.get('b2b-order-bar')) {
  customElements.define('b2b-order-bar', class B2BOrderBar extends HTMLElement {
    constructor() {
      super();
      this.countEl = this.querySelector('.b2b-order-bar__count');
      this.totalEl = this.querySelector('.b2b-order-bar__total');
    }

    connectedCallback() {
      this.refresh();

      // Also refresh whenever any cart update event fires
      if (typeof subscribe === 'function') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          // Small delay to let Shopify process the cart update
          setTimeout(() => this.refresh(), 300);
        });
      }
    }

    async refresh() {
      try {
        const response = await fetch(window.routes.cart_url + '.js', {
          headers: { 'Accept': 'application/json' }
        });
        const cart = await response.json();

        const itemCount = cart.item_count || 0;
        const total = cart.total_price || 0;

        // Update displays
        if (this.countEl) {
          this.countEl.textContent = itemCount + (itemCount === 1 ? ' item' : ' items');
        }

        if (this.totalEl) {
          this.totalEl.textContent = this.formatMoney(total);
        }

        // Show/hide bar
        if (itemCount > 0) {
          this.classList.add('b2b-order-bar--visible');
        } else {
          this.classList.remove('b2b-order-bar--visible');
        }
      } catch (err) {
        console.error('Failed to fetch cart:', err);
      }
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      }
      const amount = (cents / 100).toFixed(2);
      return '$' + amount;
    }
  });
}


/**
 * B2B Collection Search
 * Instant client-side filtering of product rows by name, SKU, vendor, type, and tags.
 */
(function() {
  const input = document.getElementById('b2b-collection-search');
  if (!input) return;

  const clearBtn = input.parentElement.querySelector('.b2b-search__clear');
  const countWrap = input.closest('.b2b-search').querySelector('.b2b-search__count');
  const countNumber = countWrap?.querySelector('.b2b-search__count-number');
  const emptyMsg = document.getElementById('b2b-search-empty');
  const rows = document.querySelectorAll('b2b-line-row.b2b-table__row');
  const totalRows = rows.length;

  let debounceTimer;

  function performSearch() {
    const query = input.value.trim().toLowerCase();
    const terms = query.split(/\s+/).filter(Boolean);

    if (!terms.length) {
      // No search — show everything, hide count
      rows.forEach(row => row.classList.remove('b2b-table__row--hidden'));
      if (clearBtn) clearBtn.style.display = 'none';
      if (countWrap) countWrap.style.display = 'none';
      if (emptyMsg) emptyMsg.classList.remove('b2b-search-empty--visible');
      return;
    }

    let matchCount = 0;

    rows.forEach(row => {
      const text = row.dataset.searchText || '';
      // Every search term must appear somewhere in the row's searchable text
      const matches = terms.every(term => text.includes(term));

      if (matches) {
        row.classList.remove('b2b-table__row--hidden');
        matchCount++;
      } else {
        row.classList.add('b2b-table__row--hidden');
      }
    });

    // Show/hide clear button
    if (clearBtn) clearBtn.style.display = '';

    // Update result count
    if (countWrap && countNumber) {
      countNumber.textContent = matchCount;
      countWrap.style.display = '';
    }

    // Show/hide "no results" message
    if (emptyMsg) {
      if (matchCount === 0) {
        emptyMsg.classList.add('b2b-search-empty--visible');
      } else {
        emptyMsg.classList.remove('b2b-search-empty--visible');
      }
    }
  }

  // Debounced input handler (150ms for snappy feel)
  input.addEventListener('input', function() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(performSearch, 150);
  });

  // Clear button
  if (clearBtn) {
    clearBtn.addEventListener('click', function() {
      input.value = '';
      performSearch();
      input.focus();
    });
  }

  // Allow Escape key to clear
  input.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      input.value = '';
      performSearch();
      input.blur();
    }
  });
})();
